import { Button } from '@components/atoms/Button/Button';
import { publicNewsletterApiContext } from '@src/lib/graphql/newsletterApi';
import {
  type NewsletterTeaserContent,
  newsletterTeaserQuery,
  newsletterTeaserSchemaQuery,
} from '@src/lib/graphql/newsletterSignupContent';
import { useQuery } from '@src/lib/graphql/urql';
import { Image, type PropsWithClassProps } from '@vgn-medien-holding/vgn-fe-components';
import { twMerge } from 'tailwind-merge';
import { defaultNewsletterTeaser } from './newsletterContent';

export const NewsletterTeaser = ({
  classProps,
  hasBackground = true,
  content,
}: PropsWithClassProps & { hasBackground?: boolean; content?: NewsletterTeaserContent }) => {
  const [schema] = useQuery<{ __type?: { fields: { name: string }[] } }, Record<string, never>>({
    query: newsletterTeaserSchemaQuery,
    variables: {},
    context: publicNewsletterApiContext,
    pause: !!content,
  });
  const fields = schema.data?.__type?.fields?.map((field) => field.name) ?? [];
  const supported = ['teaser_headline', 'teaser_text', 'teaser_cta_label', 'teaser_image'].every((field) =>
    fields.includes(field),
  );
  const [result] = useQuery<{ newsletter_overviews?: NewsletterTeaserContent[] }, Record<string, never>>({
    query: newsletterTeaserQuery,
    variables: {},
    context: publicNewsletterApiContext,
    pause: !!content || !supported,
  });
  const copy = content ?? result.data?.newsletter_overviews?.find(Boolean);
  return (
    <div
      className={twMerge(
        // Panel is always dark (surface), so force dark text colors on light layouts (e.g. articles)
        'dark relative mx-4 overflow-hidden rounded-xl px-5 py-14 text-foreground',
        'bg-surface/20',
        classProps?.root,
      )}
    >
      {hasBackground && (
        <Image
          src={copy?.teaser_image?.url || '/assets/tvm_assets/bg-newsletter.png'}
          fill
          alt=""
          sizes="100vw"
          copyright={copy?.teaser_image?.copyright_text || ''}
          focus={copy?.teaser_image?.focal_point}
          classProps={{ container: 'absolute inset-0', root: 'object-cover' }}
        />
      )}
      <div className="relative mx-auto flex max-w-[788px] flex-col gap-8 border-surface/60 border-y-0.5 bg-surface/70 p-6 backdrop-blur-xl md:flex-row md:items-center md:justify-between lg:p-12">
        <div className="md:basis-1/2">
          <h2 className="font-herokid font-medium text-3xl text-black leading-header md:text-4xl dark:text-foreground">
            {copy?.teaser_headline || defaultNewsletterTeaser.teaser_headline}
          </h2>
          <div
            className="prose space-y-2 pt-2.5 text-base text-gray-600 leading-body dark:text-foreground/60"
            dangerouslySetInnerHTML={{ __html: copy?.teaser_text || defaultNewsletterTeaser.teaser_text }}
          />
        </div>
        <Button
          title={copy?.teaser_cta_label || defaultNewsletterTeaser.teaser_cta_label}
          buttonStyle="primary"
          link="/newsletter"
          classProps={{ root: 'w-full self-start md:w-[336px] md:self-center' }}
        />
      </div>
    </div>
  );
};
